import DebugHelper from './DebugHelper.js';

export default class ErrorHandler {
    /**
     * 处理错误并显示用户友好的提示
     * @param {Error|string} error - 错误对象或错误消息
     * @param {string} context - 错误发生的上下文
     * @param {Phaser.Scene} scene - 场景对象（可选，用于显示UI）
     * @returns {string} 用户友好的错误消息
     */
    static handleError(error, context = '未知操作', scene = null) {
        let errorMessage = '';
        let errorDetails = '';
        
        // 提取错误信息
        if (error instanceof Error) {
            errorMessage = error.message || '发生未知错误';
            errorDetails = error.stack || '';
        } else if (typeof error === 'string') {
            errorMessage = error;
        } else {
            errorMessage = '发生未知错误';
        }
        
        // 记录错误
        DebugHelper.error(`[${context}] ${errorMessage}`, errorDetails);
        
        // 生成用户友好的消息
        const userMessage = this.getUserFriendlyMessage(errorMessage, context);
        
        // 如果提供了场景，显示错误提示
        if (scene) {
            this.showErrorUI(scene, userMessage);
        }
        
        return userMessage;
    }
    
    /**
     * 将技术错误消息转换为用户友好的消息
     * @param {string} errorMessage - 原始错误消息
     * @param {string} context - 错误上下文
     * @returns {string} 用户友好的消息
     */
    static getUserFriendlyMessage(errorMessage, context) {
        // 常见错误消息映射
        const errorMap = {
            'Failed to fetch': '网络连接失败，请检查网络设置',
            'Network request failed': '网络请求失败，请稍后重试',
            'localStorage is not available': '浏览器不支持本地存储，无法保存游戏',
            'QuotaExceededError': '存储空间不足，请清理浏览器缓存',
            'SyntaxError': '数据格式错误，请重新加载游戏',
            'TypeError': '操作失败，请刷新页面重试',
            'ReferenceError': '游戏资源加载失败，请刷新页面',
            'Cannot read property': '数据读取失败，请重新加载',
            'Cannot set property': '数据保存失败，请重试'
        };
        
        // 查找匹配的错误消息
        for (const [key, value] of Object.entries(errorMap)) {
            if (errorMessage.includes(key)) {
                return value;
            }
        }
        
        // 根据上下文生成默认消息
        const contextMessages = {
            '资源加载': '游戏资源加载失败，请刷新页面重试',
            '存档保存': '游戏保存失败，请检查浏览器存储权限',
            '存档加载': '存档加载失败，存档可能已损坏',
            '网络连接': '网络连接失败，请检查网络设置',
            '战斗系统': '战斗系统错误，游戏将继续运行',
            '场景切换': '场景切换失败，请刷新页面',
            '数据读取': '数据读取失败，请重新加载游戏'
        };
        
        return contextMessages[context] || `操作失败：${errorMessage}`;
    }
    
    /**
     * 显示错误UI提示
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} message - 错误消息
     */
    static showErrorUI(scene, message) {
        if (!scene || !scene.add) return;
        
        try {
            // 创建错误提示背景
            const graphics = scene.add.graphics();
            graphics.fillStyle(0x000000, 0.8);
            graphics.fillRect(0, 0, scene.cameras.main.width, scene.cameras.main.height);
            graphics.setScrollFactor(0);
            graphics.setDepth(10000);
            
            // 创建错误提示文本
            const text = scene.add.text(
                scene.cameras.main.width / 2,
                scene.cameras.main.height / 2,
                `错误\n\n${message}\n\n按任意键继续`,
                {
                    fontSize: '20px',
                    fill: '#ff0000',
                    fontFamily: 'Arial',
                    align: 'center',
                    wordWrap: { width: scene.cameras.main.width - 100 }
                }
            );
            text.setOrigin(0.5);
            text.setScrollFactor(0);
            text.setDepth(10001);
            
            // 添加点击或按键关闭
            const closeError = () => {
                graphics.destroy();
                text.destroy();
                if (scene.input) {
                    scene.input.keyboard.off('keydown', closeError);
                }
            };
            
            if (scene.input && scene.input.keyboard) {
                scene.input.keyboard.once('keydown', closeError);
            }
            
            // 3秒后自动关闭
            scene.time.delayedCall(3000, closeError);
        } catch (error) {
            // 如果UI创建失败，至少记录错误
            DebugHelper.error('显示错误UI失败', error);
        }
    }
    
    /**
     * 安全执行函数，捕获所有错误
     * @param {Function} fn - 要执行的函数
     * @param {string} context - 上下文
     * @param {*} defaultValue - 错误时的默认返回值
     * @returns {*} 函数返回值或默认值
     */
    static safeExecute(fn, context = '未知操作', defaultValue = null) {
        try {
            return fn();
        } catch (error) {
            this.handleError(error, context);
            return defaultValue;
        }
    }
    
    /**
     * 安全执行异步函数
     * @param {Function} fn - 异步函数
     * @param {string} context - 上下文
     * @param {*} defaultValue - 错误时的默认返回值
     * @returns {Promise} Promise对象
     */
    static async safeExecuteAsync(fn, context = '未知操作', defaultValue = null) {
        try {
            return await fn();
        } catch (error) {
            this.handleError(error, context);
            return defaultValue;
        }
    }
}

